import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import {
  ApiError,
  api,
  backingKindLabels,
  formatBytes,
  type ImportPreview,
  type ImportResult,
  type ImportSelection,
  type ImportStatementPreview,
  type ImportTransactionPreview,
} from '../api.js';
import { pageStyles } from '../shared-styles.js';

type Step = 'files' | 'preview' | 'result';

/**
 * Three-step import of bank statement exports: drop CSV and PDF files, check
 * the parsed statements, import the chosen ones. Dispatches `imported` once
 * something was written. dialog.show().
 */
@customElement('bank-import-dialog')
export class BankImportDialog extends LitElement {
  static styles = [
    pageStyles,
    css`
      .dropzone {
        border: 2px dashed var(--wa-color-neutral-border-normal, #bbb);
        border-radius: var(--wa-border-radius-m, 6px);
        padding: 2rem 1rem;
        text-align: center;
        color: var(--wa-color-text-quiet, #666);
        margin-bottom: 1rem;
      }
      .dropzone.drag {
        border-color: var(--wa-color-brand-border-loud, #36c);
        background: var(--wa-color-brand-fill-quiet, #eef3ff);
      }
      .dropzone input {
        margin-top: 0.75rem;
      }
      .statement {
        border: 1px solid var(--wa-color-neutral-border-quiet, #ddd);
        border-radius: var(--wa-border-radius-m, 6px);
        padding: 0.75rem 1rem;
        margin-bottom: 1rem;
      }
      .statement header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
      }
      .statement header .title {
        font-weight: 600;
      }
      .summary {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
        gap: 0.25rem 1rem;
        margin: 0.5rem 0;
        font-size: 0.9rem;
      }
      .summary span {
        display: block;
        color: var(--wa-color-text-quiet, #666);
        font-size: 0.8rem;
      }
      .statement table {
        font-size: 0.85rem;
      }
      .statement wa-select {
        max-width: 28rem;
      }
      td.desc {
        max-width: 22rem;
        min-width: 10rem;
        white-space: normal;
        overflow-wrap: anywhere;
      }
      .files li {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        margin: 0.25rem 0;
      }
      .files {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      @media (max-width: 640px) {
        td.desc {
          max-width: 12rem;
        }
      }
    `,
  ];

  @state() private isOpen = false;
  @state() private step: Step = 'files';
  @state() private files: File[] = [];
  @state() private dragging = false;
  @state() private preview: ImportPreview | null = null;
  @state() private selected = new Set<string>();
  @state() private pdfChoice: Record<string, string | null> = {};
  @state() private result: ImportResult | null = null;
  @state() private busy = false;
  @state() private error = '';
  private imported = false;

  show() {
    this.step = 'files';
    this.files = [];
    this.preview = null;
    this.result = null;
    this.selected = new Set();
    this.pdfChoice = {};
    this.error = '';
    this.imported = false;
    this.isOpen = true;
  }

  private onHidden = (e: Event) => {
    if (e.target !== e.currentTarget) return;
    this.isOpen = false;
    if (this.imported) this.dispatchEvent(new CustomEvent('imported', { bubbles: true, composed: true }));
  };

  // ---- step 1: files ----

  private addFiles(list: FileList | File[] | null) {
    if (!list) return;
    const known = new Set(this.files.map((f) => f.name));
    const added = Array.from(list).filter((f) => !known.has(f.name));
    this.files = [...this.files, ...added];
  }

  private removeFile(name: string) {
    this.files = this.files.filter((f) => f.name !== name);
  }

  private onDrop(e: DragEvent) {
    e.preventDefault();
    this.dragging = false;
    this.addFiles(e.dataTransfer?.files ?? null);
  }

  private formData(): FormData {
    const fd = new FormData();
    for (const f of this.files) fd.append('files', f, f.name);
    return fd;
  }

  private async runPreview() {
    this.busy = true;
    this.error = '';
    try {
      const p = await api.upload<ImportPreview>('/api/bank/imports/preview', this.formData());
      this.preview = p;
      this.selected = new Set(p.statements.filter((s) => s.importable).map((s) => s.key));
      const choice: Record<string, string | null> = {};
      for (const s of p.statements) choice[s.key] = s.pdf;
      this.pdfChoice = choice;
      this.step = 'preview';
    } catch (err) {
      this.error = this.describe(err);
    } finally {
      this.busy = false;
    }
  }

  // ---- step 2: preview ----

  private toggle(key: string, on: boolean) {
    const next = new Set(this.selected);
    if (on) next.add(key);
    else next.delete(key);
    this.selected = next;
  }

  private async runImport() {
    if (this.selected.size === 0) return;
    this.busy = true;
    this.error = '';
    try {
      const selection: ImportSelection = {
        statements: Array.from(this.selected).map((key) => ({ key, pdf: this.pdfChoice[key] ?? '' })),
      };
      const fd = this.formData();
      fd.append('selection', JSON.stringify(selection));
      this.result = await api.upload<ImportResult>('/api/bank/imports', fd);
      this.imported = this.result.imported.length > 0;
      this.step = 'result';
    } catch (err) {
      this.error = this.describe(err);
    } finally {
      this.busy = false;
    }
  }

  private describe(err: unknown): string {
    if (err instanceof ApiError) {
      const hint = Object.values(err.fields)[0];
      return hint ? `${err.message}: ${hint}` : err.message;
    }
    return (err as Error).message;
  }

  // ---- rendering ----

  render() {
    const titles: Record<Step, string> = { files: '1/3 Files', preview: '2/3 Check', result: '3/3 Done' };
    return html`
      <wa-dialog label=${`Import statements · ${titles[this.step]}`} ?open=${this.isOpen} @wa-after-hide=${this.onHidden} style="--width: min(72rem, calc(100vw - 1.5rem));">
        ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
        ${this.step === 'files' ? this.renderFiles() : this.step === 'preview' ? this.renderPreview() : this.renderResult()}
        <div slot="footer" class="footer">${this.renderFooter()}</div>
      </wa-dialog>
    `;
  }

  private renderFooter() {
    switch (this.step) {
      case 'files':
        return html`
          <wa-button appearance="outlined" @click=${() => (this.isOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" ?disabled=${this.files.length === 0} ?loading=${this.busy} @click=${this.runPreview}>Preview</wa-button>
        `;
      case 'preview':
        return html`
          <wa-button appearance="outlined" ?disabled=${this.busy} @click=${() => (this.step = 'files')}>Back</wa-button>
          <wa-button variant="brand" ?disabled=${this.selected.size === 0} ?loading=${this.busy} @click=${this.runImport}>
            Import ${this.selected.size} ${this.selected.size === 1 ? 'statement' : 'statements'}
          </wa-button>
        `;
      default:
        return html`<wa-button variant="brand" @click=${() => (this.isOpen = false)}>Close</wa-button>`;
    }
  }

  private renderFiles() {
    return html`
      <div
        class=${this.dragging ? 'dropzone drag' : 'dropzone'}
        @dragover=${(e: DragEvent) => {
          e.preventDefault();
          this.dragging = true;
        }}
        @dragleave=${() => (this.dragging = false)}
        @drop=${this.onDrop}
      >
        <div>Drop the bank's CSV exports and their PDF statements here</div>
        <input type="file" multiple accept=".csv,.pdf" @change=${(e: Event) => this.addFiles((e.target as HTMLInputElement).files)} />
      </div>
      ${this.files.length
        ? html`
            <ul class="files">
              ${this.files.map(
                (f) => html`
                  <li>
                    <span>${f.name}</span>
                    <span class="muted">${formatBytes(f.size)}</span>
                    <wa-button size="small" appearance="plain" @click=${() => this.removeFile(f.name)}>Remove</wa-button>
                  </li>
                `,
              )}
            </ul>
          `
        : html`<p class="muted">A PDF is paired with the CSV of the same file name; unpaired PDFs can be attached later from the Statements view.</p>`}
    `;
  }

  private renderPreview() {
    const p = this.preview;
    if (!p) return nothing;
    const pdfs = p.files.filter((f) => f.kind === 'pdf');
    return html`
      <wa-callout variant="neutral">
        <div class="check-lines">
          ${p.files.map(
            (f) => html`
              <div>
                <wa-tag size="small" variant=${f.kind === 'unknown' ? 'danger' : 'neutral'}>${f.kind}</wa-tag>
                ${f.name} <span class="muted">${formatBytes(f.size_bytes)}</span>
                ${f.paired_with ? html`<span class="muted">· paired with ${f.paired_with}</span>` : nothing}
                ${f.error ? html`<span class="warn">· ${f.error}</span>` : nothing}
              </div>
            `,
          )}
        </div>
      </wa-callout>
      ${p.statements.length === 0 ? html`<div class="empty">No statements found in the uploaded files.</div>` : nothing}
      ${p.statements.map((s) => this.renderStatement(s, pdfs.map((f) => f.name)))}
    `;
  }

  private renderStatement(s: ImportStatementPreview, pdfs: string[]) {
    const checked = this.selected.has(s.key);
    return html`
      <section class="statement">
        <header>
          <wa-checkbox ?checked=${checked} ?disabled=${!s.importable} @change=${(e: Event) => this.toggle(s.key, (e.target as HTMLInputElement).checked)}></wa-checkbox>
          <span class="title">${s.bank_account ? s.bank_account.label : html`<span class="warn">unknown account ${s.iban}</span>`}</span>
          <span>No. ${s.statement_number} · ${s.statement_date} · ${s.currency}</span>
          ${s.already_imported ? html`<wa-tag size="small" variant="warning">already imported</wa-tag>` : nothing}
          ${s.period_from ? html`<span class="muted">export ${s.period_from} – ${s.period_to}</span>` : nothing}
          <span class="spacer"></span>
          <span class="muted">${s.file}</span>
        </header>
        <div class="summary">
          <div><span>Opening</span>${s.opening_balance}</div>
          <div><span>Debits</span>${s.total_debits}</div>
          <div><span>Credits</span>${s.total_credits}</div>
          <div><span>Fees</span>${s.total_fees}</div>
          <div><span>Closing</span>${s.closing_balance}</div>
          <div><span>Rows</span>${s.transactions.length} (${s.debit_count} debit, ${s.credit_count} credit)</div>
        </div>
        ${s.errors.length ? html`<wa-callout variant="danger"><div class="check-lines">${s.errors.map((e) => html`<div>${e}</div>`)}</div></wa-callout>` : nothing}
        ${s.warnings.length
          ? html`<wa-callout variant="warning"><div class="check-lines">${s.warnings.map((w) => html`<div>${w.message}</div>`)}</div></wa-callout>`
          : nothing}
        ${s.importable
          ? html`
              <wa-select
                label="PDF to attach"
                size="small"
                with-clear
                .value=${this.pdfChoice[s.key] ?? ''}
                @change=${(e: Event) => (this.pdfChoice = { ...this.pdfChoice, [s.key]: (e.target as HTMLSelectElement).value || null })}
              >
                ${pdfs.map((name) => html`<wa-option value=${name}>${name}</wa-option>`)}
              </wa-select>
            `
          : nothing}
        <div class="table-wrap"><table class="entries">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Counterparty</th>
              <th>Description</th>
              <th class="num">Debit</th>
              <th class="num">Credit</th>
              <th class="num">Fee</th>
              <th>Code</th>
              <th>Backing</th>
            </tr>
          </thead>
          <tbody>
            ${s.transactions.map((t) => this.renderRow(t))}
          </tbody>
        </table></div>
      </section>
    `;
  }

  private renderRow(t: ImportTransactionPreview) {
    return html`
      <tr>
        <td class="muted">${t.seq}</td>
        <td>${t.value_date}</td>
        <td>
          ${t.counterparty_name || html`<span class="muted">—</span>`}
          ${t.counterparty_account ? html`<span class="sub mono">${t.counterparty_account}</span>` : nothing}
        </td>
        <td class="desc" title=${t.description}>${t.description}</td>
        <td class="num">${t.direction === 'debit' && Number(t.amount) ? t.amount : ''}</td>
        <td class="num">${t.direction === 'credit' ? t.amount : ''}</td>
        <td class="num">${Number(t.fee) ? t.fee : ''}</td>
        <td class="muted">${t.code || ''}</td>
        <td>
          ${backingTag(t.backing_kind, t.employee_name)}
          <span class="sub">${t.prefill_reason}</span>
        </td>
      </tr>
    `;
  }

  private renderResult() {
    const r = this.result;
    if (!r) return nothing;
    const p = this.preview;
    const label = (key: string) => {
      const s = p?.statements.find((x) => x.key === key);
      return s ? `Statement ${s.statement_number} · ${s.bank_account?.label ?? s.iban} · ${s.statement_date}` : key;
    };
    return html`
      ${r.imported.length ? nothing : html`<p class="muted">Nothing was imported.</p>`}
      <div class="check-lines">
        ${r.imported.map(
          (i) => html`
            <div>
              <wa-tag size="small" variant="success">imported</wa-tag>
              ${label(i.key)} · ${i.transactions} transactions
              ${i.pdf_document_id ? html`· PDF attached` : i.pdf_error ? html`<span class="warn">· PDF not attached: ${i.pdf_error}</span>` : html`<span class="muted">· no PDF</span>`}
            </div>
          `,
        )}
        ${r.skipped.map(
          (s) => html`
            <div>
              <wa-tag size="small" variant="neutral">skipped</wa-tag>
              ${label(s.key)} · ${s.reason}
            </div>
          `,
        )}
      </div>
    `;
  }
}

/** The tag shown for a backing kind, with the employee's name when set. */
/** The backing of a transaction as a tag; taxOf names the transfer a tax row belongs to. */
export function backingTag(kind: string, employeeName?: string | null, outstanding = kind === 'document', taxOf?: string | null) {
  switch (kind) {
    case 'document':
      return outstanding ? html`<wa-tag size="small" variant="warning">Needs document</wa-tag>` : html`<wa-tag size="small" variant="success">Document</wa-tag>`;
    case 'employee':
      return html`<wa-tag size="small" variant="brand">${employeeName ?? backingKindLabels.employee}</wa-tag>`;
    case 'tax':
      if (!taxOf) return html`<wa-tag size="small" variant=${outstanding ? 'warning' : 'neutral'}>${outstanding ? 'Tax, transfer not found' : 'Tax'}</wa-tag>`;
      return outstanding
        ? html`<wa-tag size="small" variant="warning">Tax, transfer needs document</wa-tag>`
        : html`<wa-tag size="small" variant="success">Tax on ${taxOf}</wa-tag>`;
    default:
      return html`<wa-tag size="small" variant="neutral">${backingKindLabels[kind as keyof typeof backingKindLabels] ?? kind}</wa-tag>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bank-import-dialog': BankImportDialog;
  }
}
